<template>
  <v-container fluid class="pa-0 chat-page">
    <v-row no-gutters class="btp-chat__workspace">
      <!-- Sidebar -->
      <v-col
        class="btp-chat__sidebar"
        :class="{ 'is-mobile-open': mobileSidebarOpen }"
      >
        <div class="btp-chat__sidebar-panel">
          <!-- Header -->
          <div class="btp-chat__brand">
            <div class="btp-chat__brand-icon">
              <v-icon size="20" color="#fff">mdi-forum-outline</v-icon>
            </div>
            <div class="btp-chat__brand-copy">
              <div class="btp-chat__brand-kicker">Centrum komunikacji</div>
              <div class="btp-chat__brand-title">{{ companyName }}</div>
            </div>
            <button
              type="button"
              class="btp-chat__sidebar-close"
              aria-label="Zamknij listę kanałów"
              @click="mobileSidebarOpen = false"
            >
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>

          <div class="btp-chat__sidebar-actions">
            <label class="btp-chat__channel-search">
              <v-icon size="17">mdi-magnify</v-icon>
              <input v-model="channelSearch" type="search" placeholder="Szukaj kanału…" />
              <button
                v-if="channelSearch"
                type="button"
                aria-label="Wyczyść wyszukiwanie kanałów"
                @click="channelSearch = ''"
              >
                <v-icon size="15">mdi-close-circle</v-icon>
              </button>
            </label>

            <button v-if="isChatAdmin" class="btp-chat__new-btn" @click="openChannelDialog()">
              <span class="btp-chat__new-btn-icon"><v-icon size="17" color="white">mdi-plus</v-icon></span>
              <span>Utwórz kanał</span>
              <v-icon size="16" color="white">mdi-arrow-right</v-icon>
            </button>
          </div>

        <!-- Channels list -->
        <div class="btp-chat__nav">
          <v-progress-linear v-if="loadingChannels" indeterminate height="2" color="#7C94FF" class="mb-2"></v-progress-linear>
          <div class="btp-chat__section-head">
            <span>Kanały</span>
            <v-icon v-if="isChatAdmin" size="12" class="btp-chat__section-add" @click="openChannelDialog()">mdi-plus</v-icon>
          </div>

          <div
            v-for="ch in filteredPublicChannels"
            :key="ch.id"
            class="btp-chat__channel"
            :class="{ 'is-active': activeChannel && activeChannel.id === ch.id, 'has-unread': unreadCounts[ch.id] }"
          >
            <button
              type="button"
              class="btp-chat__channel-select"
              :aria-current="activeChannel && activeChannel.id === ch.id ? 'true' : null"
              @click="selectChannel(ch)"
            >
              <span class="btp-chat__channel-icon" :style="{ color: (activeChannel && activeChannel.id === ch.id) ? '#60a5fa' : (ch.color || '#94a3b8') }">
                {{ ch.icon || '#' }}
              </span>
              <span class="btp-chat__channel-name">{{ ch.name }}</span>
              <span v-if="unreadCounts[ch.id]" class="btp-chat__unread-pill">{{ unreadCounts[ch.id] }}</span>
            </button>
            <v-menu v-if="!ch.isSystem && isChatAdmin" offset-y left>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click.stop class="btp-chat__channel-menu">
                  <v-icon size="12" color="#64748b">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="openChannelDialog(ch)"><v-list-item-title>{{ $t('common.edit') }}</v-list-item-title></v-list-item>
                <v-list-item @click="confirmDeleteChannel(ch)"><v-list-item-title class="red--text">{{ $t('common.delete') }}</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>

          <div class="btp-chat__section-head mt-4" v-if="filteredPrivateChannels.length || (isChatAdmin && !channelSearch)">
            <span>Prywatne wiadomości</span>
          </div>

          <div
            v-for="ch in filteredPrivateChannels"
            :key="ch.id"
            class="btp-chat__channel"
            :class="{ 'is-active': activeChannel && activeChannel.id === ch.id, 'has-unread': unreadCounts[ch.id] }"
          >
            <button
              type="button"
              class="btp-chat__channel-select"
              :aria-current="activeChannel && activeChannel.id === ch.id ? 'true' : null"
              @click="selectChannel(ch)"
            >
              <span class="btp-chat__channel-icon" :style="{ color: ch.color || '#42D787' }">{{ ch.icon || '🔒' }}</span>
              <span class="btp-chat__channel-name">{{ ch.name }}</span>
              <span v-if="unreadCounts[ch.id]" class="btp-chat__unread-pill">{{ unreadCounts[ch.id] }}</span>
            </button>
            <v-menu v-if="!ch.isSystem && isChatAdmin" offset-y left>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" @click.stop class="btp-chat__channel-menu">
                  <v-icon size="12" color="#64748b">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="openChannelDialog(ch)"><v-list-item-title>{{ $t('common.edit') }}</v-list-item-title></v-list-item>
                <v-list-item @click="confirmDeleteChannel(ch)"><v-list-item-title class="red--text">{{ $t('common.delete') }}</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>

          <div v-if="channelSearch && !filteredPublicChannels.length && !filteredPrivateChannels.length" class="btp-chat__nav-empty">
            <v-icon size="22">mdi-magnify-close</v-icon>
            <span>Nie znaleziono kanału</span>
          </div>
          <div v-else-if="!loadingChannels && !channels.length" class="btp-chat__nav-empty">
            <v-icon size="22">mdi-forum-remove-outline</v-icon>
            <span>Brak dostępnych kanałów</span>
          </div>
        </div>

        <!-- User card footer -->
        <div class="btp-chat__user">
          <div class="btp-chat__user-avatar">
            <span>{{ userInitials }}</span>
            <span class="btp-chat__user-dot"></span>
          </div>
          <div class="flex-grow-1 min-width-0">
            <div class="d-flex align-center" style="gap: 6px;">
              <span class="btp-chat__user-name">{{ userName }}</span>
              <span v-if="isChatAdmin" class="btp-chat__user-chip">Admin</span>
            </div>
            <div class="btp-chat__user-status">
              <span class="btp-chat__connection-dot" :class="{ online: socketConnected }"></span>
              {{ socketConnected ? 'Połączono' : 'Tryb awaryjny' }}
            </div>
          </div>
        </div>
      </div>
      </v-col>

      <button
        v-if="mobileSidebarOpen"
        type="button"
        class="btp-chat__sidebar-backdrop"
        aria-label="Zamknij listę kanałów"
        @click="mobileSidebarOpen = false"
      ></button>

      <!-- Main chat -->
      <v-col cols="9" lg="10" class="btp-chat__main">
        <!-- Header -->
        <header class="btp-chat__header">
          <button
            type="button"
            class="btp-chat__mobile-menu"
            aria-label="Pokaż kanały"
            @click="mobileSidebarOpen = true"
          >
            <v-badge :content="totalUnread" :value="totalUnread > 0" color="#EF5350" overlap>
              <v-icon size="21">mdi-menu</v-icon>
            </v-badge>
          </button>

          <div class="btp-chat__header-channel">
            <div class="btp-chat__header-channel-icon" :style="activeChannel ? { color: activeChannel.color || '#7C94FF' } : {}">
              {{ activeChannel ? (activeChannel.icon || '#') : '—' }}
            </div>
            <div class="btp-chat__header-copy">
              <span class="btp-chat__header-name">{{ activeChannel ? activeChannel.name : 'Wybierz kanał' }}</span>
              <span class="btp-chat__header-desc">
                {{ activeChannel ? (activeChannel.description || 'Kanał firmowy') : 'Wybierz rozmowę z listy kanałów' }}
              </span>
            </div>
          </div>

          <div class="btp-chat__header-actions">
            <label class="btp-chat__search" :class="{ 'is-disabled': !activeChannel }">
              <v-icon size="16">mdi-magnify</v-icon>
              <input
                v-model="headerSearch"
                type="search"
                :disabled="!activeChannel"
                :placeholder="activeChannel ? 'Szukaj w rozmowie…' : 'Najpierw wybierz kanał'"
              />
              <button
                v-if="headerSearch"
                type="button"
                aria-label="Wyczyść wyszukiwanie wiadomości"
                @click="headerSearch = ''"
              >
                <v-icon size="15">mdi-close-circle</v-icon>
              </button>
            </label>
            <div class="btp-chat__connection" :class="{ online: socketConnected }">
              <span></span>
              {{ socketConnected ? 'Na żywo' : 'Łączenie' }}
            </div>
            <button
              v-if="activeChannel && isChatAdmin"
              class="btp-chat__icon-btn"
              title="Ustawienia kanału"
              aria-label="Ustawienia kanału"
              @click="openChannelDialog(activeChannel)"
            >
              <v-icon size="18">mdi-cog-outline</v-icon>
            </button>
          </div>
        </header>

        <!-- Messages canvas -->
        <section ref="messagesArea" class="btp-chat__messages" role="log" aria-live="polite">
          <div v-if="loadingMessages" class="btp-chat__loading">
            <div class="btp-chat__loading-orbit">
              <v-progress-circular indeterminate size="30" width="3" color="#7C94FF"></v-progress-circular>
            </div>
            <span>Wczytujemy rozmowę…</span>
          </div>

          <div v-else-if="messageError" class="btp-chat__empty btp-chat__empty--search">
            <div class="btp-chat__empty-halo btp-chat__empty-halo--small btp-chat__empty-halo--error">
              <v-icon size="38">mdi-cloud-alert-outline</v-icon>
            </div>
            <h2 class="btp-chat__empty-title">Nie udało się wczytać rozmowy</h2>
            <p class="btp-chat__empty-text">Sprawdź połączenie i spróbuj ponownie.</p>
            <button class="btp-chat__btn btp-chat__btn--primary" @click="selectChannel(activeChannel)">
              <v-icon left size="17" color="#fff">mdi-refresh</v-icon>
              Spróbuj ponownie
            </button>
          </div>

          <!-- No channel selected -->
          <div v-else-if="!activeChannel" class="btp-chat__empty">
            <div class="btp-chat__empty-halo">
              <v-icon size="54">mdi-forum-outline</v-icon>
            </div>
            <span class="btp-chat__empty-kicker">BTP Connect</span>
            <h2 class="btp-chat__empty-title">Wszystkie rozmowy w jednym miejscu</h2>
            <p class="btp-chat__empty-text">Wybierz kanał, aby sprawdzić ustalenia zespołu, przekazać informację lub wysłać dokument.</p>
            <button class="btp-chat__empty-mobile-action" @click="mobileSidebarOpen = true">
              <v-icon left size="18" color="#fff">mdi-view-list-outline</v-icon>
              Pokaż kanały
            </button>
          </div>

          <!-- Empty channel state -->
          <div v-else-if="messages.length === 0" class="btp-chat__empty">
            <div class="btp-chat__empty-halo">
              <v-icon size="54">mdi-message-text-fast-outline</v-icon>
            </div>
            <span class="btp-chat__empty-kicker">#{{ activeChannel.name }}</span>
            <h2 class="btp-chat__empty-title">Rozpocznij rozmowę</h2>
            <p class="btp-chat__empty-text">
              Ten kanał jest jeszcze pusty. Napisz pierwszą wiadomość lub dodaj plik, aby rozpocząć współpracę.
            </p>
            <button class="btp-chat__btn btp-chat__btn--primary" @click="focusComposer">
              <v-icon left size="17" color="#fff">mdi-pencil-outline</v-icon>
              Napisz wiadomość
            </button>
          </div>

          <div v-else-if="headerSearch && !displayedMessages.length" class="btp-chat__empty btp-chat__empty--search">
            <div class="btp-chat__empty-halo btp-chat__empty-halo--small">
              <v-icon size="38">mdi-text-search</v-icon>
            </div>
            <h2 class="btp-chat__empty-title">Brak wyników</h2>
            <p class="btp-chat__empty-text">Nie znaleźliśmy wiadomości pasujących do „{{ headerSearch }}”.</p>
            <button class="btp-chat__btn btp-chat__btn--outline" @click="headerSearch = ''">Wyczyść wyszukiwanie</button>
          </div>

          <!-- Messages -->
          <template v-else>
            <div v-for="(msg, i) in displayedMessages" :key="msg.id">
              <div v-if="showDateSeparator(i, displayedMessages)" class="btp-chat__date-separator">
                <span>{{ formatDateLabel(msg.createdAt) }}</span>
              </div>
              <!-- System message -->
              <div v-if="msg.type === 'system'" class="btp-chat__system">
                <span>{{ msg.text }}</span>
              </div>
              <!-- Regular message -->
              <div v-else class="btp-chat__msg" :class="{ 'is-new-group': isNewGroup(i, displayedMessages), 'is-own': isOwnMessage(msg) }">
                <div v-if="isNewGroup(i, displayedMessages)" class="btp-chat__msg-avatar" :style="authorAvatar(msg) ? {} : { background: getAuthorColor(msg).bg, color: getAuthorColor(msg).fg }">
                  <img v-if="authorAvatar(msg)" :src="authorAvatar(msg)" alt="avatar" class="btp-chat__msg-avatar-img" />
                  <template v-else>{{ getAuthorInitials(msg) }}</template>
                </div>
                <div v-else class="btp-chat__msg-avatar-spacer"></div>
                <div style="flex: 1; min-width: 0;">
                  <div v-if="isNewGroup(i, displayedMessages)" class="btp-chat__msg-header">
                    <span class="btp-chat__msg-author">{{ getAuthorName(msg) }}</span>
                    <span class="btp-chat__msg-time">{{ formatTime(msg.createdAt) }}</span>
                  </div>
                  <!-- Text -->
                  <div v-if="msg.type === 'text'" class="btp-chat__msg-text" v-html="formatText(msg.text)"></div>
                  <!-- Image -->
                  <div v-if="msg.type === 'img'">
                    <div v-if="msg.text && msg.text !== msg.fileName" class="btp-chat__msg-text mb-1">{{ msg.text }}</div>
                    <img
                      v-if="msg.fileId && loadedImages[msg.fileId]"
                      :src="loadedImages[msg.fileId]"
                      :alt="msg.fileName || 'Obraz w wiadomości'"
                      class="btp-chat__msg-img"
                      title="Pobierz obraz"
                      @click="downloadFile(msg.fileId, msg.fileName)"
                    >
                    <div v-else-if="msg.fileId" class="btp-chat__msg-img-placeholder">
                      <v-progress-circular indeterminate size="20" width="2" color="grey"></v-progress-circular>
                    </div>
                  </div>
                  <!-- File -->
                  <div
                    v-if="msg.type === 'file'"
                    class="btp-chat__msg-file"
                    @click="downloadFile(msg.fileId, msg.fileName)"
                  >
                    <div class="btp-chat__msg-file-icon">
                      <v-icon size="20" color="#f87171">mdi-file-document</v-icon>
                    </div>
                    <div class="flex-grow-1 min-width-0">
                      <div class="btp-chat__msg-file-name">{{ msg.fileName }}</div>
                      <div class="btp-chat__msg-file-meta">{{ msg.fileSize }}</div>
                    </div>
                    <v-icon size="18" color="#64748b">mdi-download</v-icon>
                  </div>
                  <!-- „Przeczytali…" tylko pod ostatnią własną wiadomością —
                       przy każdej byłby szum, a i tak liczy się najnowsza. -->
                  <div v-if="isLastOwnMessage(i)" class="btp-chat__reads">
                    <v-icon size="13" :color="readersFor(msg).length ? '#4ade80' : '#64748b'">
                      {{ readersFor(msg).length ? 'mdi-check-all' : 'mdi-check' }}
                    </v-icon>
                    <span>{{ readsLabel(msg) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Typing indicator -->
          <div v-if="typingUsers.length" class="btp-chat__typing">
            <div class="typing-dots"><span></span><span></span><span></span></div>
            <span>{{ typingText }}</span>
          </div>
        </section>

        <!-- Input area -->
        <footer v-if="activeChannel" class="btp-chat__footer">
          <!-- Attachments preview -->
          <div v-if="attachments.length" class="btp-chat__attach-row">
            <div v-for="(att, i) in attachments" :key="i" class="btp-chat__attach">
              <img v-if="att.type === 'img'" :src="att.url" :alt="att.name || 'Podgląd obrazu'" />
              <div v-else class="btp-chat__attach-file">
                <v-icon small>mdi-file-document-outline</v-icon>
                <span>{{ att.ext }}</span>
              </div>
              <div class="btp-chat__attach-name">{{ att.name }}</div>
              <button class="btp-chat__attach-remove" aria-label="Usuń załącznik" @click="removeAttachment(i)">
                <v-icon size="10" color="white">mdi-close</v-icon>
              </button>
            </div>
          </div>

          <!-- Input box -->
          <div class="btp-chat__input-box">
            <!-- Formatting toolbar -->
            <div class="btp-chat__input-toolbar">
              <button class="btp-chat__tool-btn" @click="insertTemplate" :title="$t('chat.template')">
                <v-icon size="17">mdi-clipboard-text-outline</v-icon>
                <span>Szablon</span>
              </button>
              <button class="btp-chat__tool-btn" @click="$refs.imgInput.click()" :title="$t('chat.photo')">
                <v-icon size="17">mdi-image-outline</v-icon>
                <span>Zdjęcie</span>
              </button>
              <button class="btp-chat__tool-btn" @click="$refs.fileInput.click()" :title="$t('chat.file')">
                <v-icon size="17">mdi-paperclip</v-icon>
                <span>Plik</span>
              </button>
            </div>

            <!-- Textarea row -->
            <div class="btp-chat__input-row">
              <textarea
                ref="chatInput"
                v-model="messageText"
                :placeholder="'Wiadomość do #' + activeChannel.name + '...'"
                rows="1"
                class="btp-chat__input"
                :disabled="sending"
                @keydown.enter.exact.prevent="sendMessage"
                @input="onTextInput"
              ></textarea>
              <button
                class="btp-chat__input-send"
                :disabled="sending || (!messageText.trim() && !attachments.length)"
                :aria-busy="sending ? 'true' : 'false'"
                aria-label="Wyślij wiadomość"
                @click="sendMessage"
              >
                <v-progress-circular v-if="sending" indeterminate size="18" width="2" color="#fff"></v-progress-circular>
                <v-icon v-else size="18" color="white">mdi-send</v-icon>
              </button>
            </div>
          </div>

          <!-- Bottom status bar -->
          <div class="btp-chat__status">
            <span><strong>Enter</strong> wysyła · <strong>Shift + Enter</strong> dodaje nową linię</span>
            <span>{{ messageText.length }} znaków</span>
          </div>

          <input type="file" ref="fileInput" multiple accept=".docx,.pdf,.xlsx,.txt" style="display: none;" @change="attachFiles">
          <input type="file" ref="imgInput" multiple accept="image/*" style="display: none;" @change="attachImages">
        </footer>
      </v-col>
    </v-row>

    <!-- Channel create/edit dialog -->
    <v-dialog v-model="channelDialog" max-width="480" persistent overlay-color="#0F1923" overlay-opacity="0.72">
      <div class="chdlg">
        <!-- Header z gradientem -->
        <div class="chdlg__header">
          <div class="chdlg__header-icon">
            <v-icon color="#fff" size="20">{{ editingChannel ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          </div>
          <div class="chdlg__header-text">
            <div class="chdlg__title">{{ editingChannel ? $t('chat.editChannel') : $t('chat.newChannel') }}</div>
            <div class="chdlg__sub">{{ editingChannel ? 'Edytujesz istniejący kanał' : 'Utwórz nowy kanał komunikacji' }}</div>
          </div>
          <v-btn icon dark @click="channelDialog = false" class="chdlg__close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Body -->
        <div class="chdlg__body">
          <div class="chdlg__group">
            <label class="chdlg__label">{{ $t('chat.channelName') }}</label>
            <v-text-field
              v-model="channelForm.name"
              :placeholder="$t('chat.channelNameHint')"
              outlined dense hide-details dark
              background-color="#0F1923"
              prepend-inner-icon="mdi-pound"
              class="chdlg__input"
            />
          </div>

          <div class="chdlg__group">
            <label class="chdlg__label">{{ $t('common.description') }}</label>
            <v-text-field
              v-model="channelForm.description"
              placeholder="Krótki opis (opcjonalny)"
              outlined dense hide-details dark
              background-color="#0F1923"
              prepend-inner-icon="mdi-text-short"
              class="chdlg__input"
            />
          </div>

          <div class="chdlg__group">
            <label class="chdlg__label">{{ $t('chat.channelType') }}</label>
            <div class="chdlg__types">
              <button
                v-for="t in channelTypes"
                :key="t.id"
                type="button"
                class="chdlg__type"
                :class="{ active: channelForm.type === t.id }"
                @click="channelForm.type = t.id"
              >
                <span class="chdlg__type-icon">{{ t.icon }}</span>
                <span class="chdlg__type-name">{{ t.label }}</span>
              </button>
            </div>
          </div>

          <div class="chdlg__group">
            <label class="chdlg__label">{{ $t('chat.channelColor') }}</label>
            <div class="chdlg__colors">
              <button
                v-for="c in channelColorOptions"
                :key="c"
                type="button"
                class="chdlg__color"
                :class="{ active: channelForm.color === c }"
                :style="{ background: c }"
                @click="channelForm.color = c"
              >
                <v-icon v-if="channelForm.color === c" size="14" color="#fff">mdi-check</v-icon>
              </button>
            </div>
          </div>

          <div class="chdlg__group">
            <label class="chdlg__label">{{ $t('chat.channelIcon') }}</label>
            <div class="chdlg__icons">
              <button
                v-for="ic in channelIconOptions"
                :key="ic"
                type="button"
                class="chdlg__icon-opt"
                :class="{ active: channelForm.icon === ic }"
                @click="channelForm.icon = ic"
              >{{ ic }}</button>
            </div>
            <v-text-field
              v-model="channelForm.icon"
              :placeholder="$t('chat.channelIconHint')"
              maxlength="4"
              outlined dense hide-details dark
              background-color="#0F1923"
              class="chdlg__input mt-2"
            />
          </div>

          <!-- Członkowie (tylko prywatne, w trybie edycji) -->
          <div class="chdlg__group" v-if="editingChannel && channelForm.type === 'private'">
            <label class="chdlg__label">Członkowie kanału</label>
            <div class="chdlg__members">
              <div v-if="loadingMembers" class="caption grey--text">Wczytuję…</div>
              <div v-else-if="!channelMembers.length" class="caption grey--text">Brak członków — dodaj kogoś z listy poniżej.</div>
              <div v-for="m in channelMembers" :key="m.id" class="chdlg__member">
                <span class="chdlg__member-name">{{ m.firstName }} {{ m.lastName }}</span>
                <button type="button" class="chdlg__member-remove" @click="removeMember(m.id)" title="Usuń">
                  <v-icon size="14" color="#EF5350">mdi-close-circle</v-icon>
                </button>
              </div>
            </div>
            <v-autocomplete
              v-model="addMemberPick"
              :items="availableMembersToAdd"
              :item-text="(a) => `${a.firstName || ''} ${a.lastName || ''}`.trim() + (a.email ? ` (${a.email})` : '')"
              item-value="id"
              placeholder="Wybierz administratora do dodania…"
              outlined dense hide-details dark clearable
              background-color="#0F1923"
              prepend-inner-icon="mdi-account-plus"
              class="chdlg__input mt-3"
              :no-data-text="'Brak dostępnych administratorów'"
              @change="onAddMemberPicked"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="chdlg__footer">
          <button type="button" class="chdlg__btn chdlg__btn--ghost" @click="channelDialog = false">
            {{ $t('common.cancel') }}
          </button>
          <button
            type="button"
            class="chdlg__btn chdlg__btn--primary"
            :disabled="saving || !channelForm.name || !channelForm.name.trim()"
            @click="saveChannel"
          >
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="#fff" class="mr-2" />
            <v-icon v-else left size="16">{{ editingChannel ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
            {{ editingChannel ? $t('common.save') : $t('chat.createChannel') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="deleteDialog" max-width="420" overlay-color="#0F1320" overlay-opacity="0.78">
      <div class="btp-chat__confirm">
        <div class="btp-chat__confirm-icon"><v-icon size="26">mdi-alert-outline</v-icon></div>
        <div>
          <h3>{{ $t('chat.confirmation') }}</h3>
          <p>{{ $t('chat.deleteChannelConfirm', { name: deleteTarget ? deleteTarget.name : '' }) }}</p>
        </div>
        <div class="btp-chat__confirm-actions">
          <button class="chdlg__btn chdlg__btn--ghost" @click="deleteDialog = false">{{ $t('common.cancel') }}</button>
          <button class="chdlg__btn btp-chat__delete-btn" @click="executeDeleteChannel">
            <v-icon left size="16" color="#fff">mdi-delete-outline</v-icon>
            {{ $t('common.delete') }}
          </button>
        </div>
      </div>
    </v-dialog>
  </v-container>
</template>

<script>
const axios = require("axios");
import { connectChat, getSocket } from '@/services/chatSocket';
import { chatState, markChannelRead } from '@/plugins/chatNotifications';

const TEMPLATES = [
  "WYDANIE\nAuto: [marka, nr rej.]\nKierowca: [imie]\nData: [DD.MM.YYYY]\nKm: [odczyt]\nStan: OK",
  "ZWROT\nAuto: [marka, nr rej.]\nKierowca: [imie]\nData: [DD.MM.YYYY]\nKm: [odczyt]\nKaucja: zwrocona",
  "SERWIS\nAuto: [marka, nr rej.]\nProblem: [opis]\nWarsztat: [nazwa]\nETA: [DD.MM.YYYY]",
  "SZKODA\nAuto: [nr rej.]\nKierowca: [imie]\nData: [DD.MM.YYYY]\nOpis: [co sie stalo]\nPolicja: [tak/nie]",
  "NOWY KIEROWCA\nImie: [imie]\nPlatforma: [Uber/Bolt]\nAuto: [nr rej.]\nStart: [DD.MM.YYYY]",
];

const AVATAR_COLORS = [
  { bg: '#dbeafe', fg: '#1d4ed8' },
  { bg: '#dcfce7', fg: '#166534' },
  { bg: '#fef3c7', fg: '#92400e' },
  { bg: '#fce7f3', fg: '#9d174d' },
  { bg: '#e0e7ff', fg: '#3730a3' },
  { bg: '#ccfbf1', fg: '#115e59' },
  { bg: '#fee2e2', fg: '#991b1b' },
  { bg: '#f3e8ff', fg: '#6b21a8' },
];

export default {
  name: "ChatPage",
  data: () => ({
    channels: [],
    activeChannel: null,
    avatarMap: {},
    messages: [],
    messageText: '',
    attachments: [],
    draftsByChannel: {},
    templateIndex: 0,
    saving: false,
    sending: false,
    loadingChannels: false,
    loadingMessages: false,
    messageError: false,
    messageRequestId: 0,
    pollInterval: null,
    lastMessageId: null,
    isChatAdmin: false,
    currentUserId: null,
    currentUser: null,
    loadedImages: {},
    imageLoadGeneration: 0,
    socketConnected: false,
    typingUsers: [],
    // Kto dokąd doczytał w aktywnym kanale: [{ userId, firstName, lastName, lastReadAt }]
    channelReads: [],
    typingTimeout: null,
    typingStopTimeout: null,
    socketError: null,

    // Channel dialog
    channelDialog: false,
    editingChannel: null,
    channelForm: { name: '', description: '', type: 'public', color: '#1976D2', icon: '#' },
    channelColorOptions: ['#1976D2', '#388E3C', '#F57C00', '#D32F2F', '#7B1FA2', '#0097A7', '#455A64', '#C2185B', '#512DA8', '#00796B'],
    channelIconOptions: ['#', '💬', '📢', '🚨', '🔒', '📦', '🚗', '🛠️', '💰', '📋', '📅', '⭐', '🏁', '⚡', '✅', '❗'],
    channelTypes: [
      { id: 'public', label: 'Publiczny', icon: '#' },
      { id: 'private', label: 'Prywatny', icon: '🔒' },
      { id: 'alert', label: 'Alarmowy', icon: '🚨' },
      { id: 'archive', label: 'Archiwum', icon: '📦' },
    ],

    // Delete
    deleteDialog: false,
    deleteTarget: null,

    // Channel members (prywatne)
    channelMembers: [],
    loadingMembers: false,
    companyAdmins: [],
    addMemberPick: null,

    headerSearch: '',
    channelSearch: '',
    mobileSidebarOpen: false,
  }),
  computed: {
    unreadCounts() {
      return chatState.unreadByChannel || {};
    },
    publicChannels() {
      return this.channels.filter((c) => c.type !== 'private');
    },
    privateChannels() {
      return this.channels.filter((c) => c.type === 'private');
    },
    filteredPublicChannels() {
      return this.filterChannels(this.publicChannels);
    },
    filteredPrivateChannels() {
      return this.filterChannels(this.privateChannels);
    },
    displayedMessages() {
      const query = this.headerSearch.trim().toLocaleLowerCase('pl');
      if (!query) return this.messages;
      return this.messages.filter((msg) => {
        const author = this.getAuthorName(msg);
        return [msg.text, msg.fileName, author]
          .filter(Boolean)
          .some((value) => String(value).toLocaleLowerCase('pl').includes(query));
      });
    },
    totalUnread() {
      return Object.values(this.unreadCounts).reduce((sum, count) => sum + Number(count || 0), 0);
    },
    userName() {
      const fn = this.currentUser?.firstName || '';
      const ln = this.currentUser?.lastName || '';
      return (fn + ' ' + ln).trim() || 'Admin';
    },
    companyName() {
      return localStorage.getItem('activeCompanyName') || 'Firma';
    },
    availableMembersToAdd() {
      const memberIds = new Set(this.channelMembers.map((m) => m.id));
      return (this.companyAdmins || []).filter((a) => !memberIds.has(a.id));
    },
    userInitials() {
      const fn = this.currentUser?.firstName || 'A';
      const ln = this.currentUser?.lastName || '';
      return (fn[0] + (ln[0] || '')).toUpperCase();
    },
    typingText() {
      if (this.typingUsers.length === 0) return '';
      if (this.typingUsers.length === 1) return this.typingUsers[0] + ' pisze...';
      if (this.typingUsers.length === 2) return this.typingUsers[0] + ' i ' + this.typingUsers[1] + ' piszą...';
      return this.typingUsers[0] + ' i ' + (this.typingUsers.length - 1) + ' innych pisze...';
    },
  },
  async mounted() {
    await this.fetchMe();
    await this.fetchAvatars();
    await this.fetchChannels();
    this.initSocket();
    if (this.channels.length > 0) {
      this.selectChannel(this.channels[0]);
    }
    this.pollInterval = setInterval(() => {
      if (document.hidden) return;
      if (this.activeChannel && !this.socketConnected) this.pollNewMessages();
    }, 5000);
  },
  beforeDestroy() {
    if (this.pollInterval) clearInterval(this.pollInterval);
    if (this.typingStopTimeout) clearTimeout(this.typingStopTimeout);
    Object.values(this._typingTimers || {}).forEach((timer) => clearTimeout(timer));
    // Zdejmij NASZ listener wiadomości (socket zostaje żywy dla powiadomień) —
    // inaczej przy powrocie na czat mnożyłyby się martwe handlery.
    const sock = getSocket();
    if (sock) {
      [
        ['connect', this._onConnect],
        ['disconnect', this._onDisconnect],
        ['new_message', this._onNewMessage],
        ['user_typing', this._onUserTyping],
        ['read_receipt', this._onReadReceipt],
        ['channel_created', this._onChannelCreated],
        ['channel_updated', this._onChannelUpdated],
        ['channel_deleted', this._onChannelDeleted],
        ['error', this._onSocketError],
      ].forEach(([event, handler]) => { if (handler) sock.off(event, handler); });
    }
    this.imageLoadGeneration++;
    Object.values(this.loadedImages).forEach((url) => URL.revokeObjectURL(url));
    const draftAttachments = Object.values(this.draftsByChannel)
      .flatMap((draft) => draft.attachments || []);
    [...this.attachments, ...draftAttachments].forEach((att) => { if (att.url) URL.revokeObjectURL(att.url); });
    // Don't disconnect - global plugin keeps the socket alive for notifications
  },
  methods: {
    filterChannels(list) {
      const query = this.channelSearch.trim().toLocaleLowerCase('pl');
      if (!query) return list;
      return list.filter((channel) => [channel.name, channel.description]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase('pl').includes(query)));
    },
    async fetchAvatars() {
      try {
        const res = await axios.get(this.apiUrl('chat/avatars'), { headers: this.authHeaders() });
        const map = {};
        (res.data || []).forEach((a) => { if (a && a.avatar) map[a.id] = a.avatar; });
        this.avatarMap = map;
      } catch (e) {
        // brak avatarów nie blokuje czatu
        this.avatarMap = {};
      }
    },
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(path) {
      return process.env.VUE_APP_ROOT_API + path;
    },

    initSocket() {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.VUE_APP_ROOT_API || '';
      const sock = connectChat(token, baseUrl);

      this._onConnect = () => {
        console.log('[ChatSocket] connected');
        this.socketConnected = true;
        if (this.activeChannel) {
          sock.emit('join_channel', { channelId: this.activeChannel.id });
        }
      };
      sock.on('connect', this._onConnect);

      this._onDisconnect = () => {
        console.log('[ChatSocket] disconnected');
        this.socketConnected = false;
      };
      sock.on('disconnect', this._onDisconnect);

      // Named handler — socket jest GLOBALNY (żyje dla powiadomień), więc inline
      // arrow z każdego mount zostawałaby jako martwy listener. Zdejmujemy nasz
      // poprzedni i rejestrujemy nowy, a przy unmount go usuwamy (beforeDestroy).
      if (this._onNewMessage) sock.off('new_message', this._onNewMessage);
      this._onNewMessage = (msg) => {
        if (!this.activeChannel || msg.channelId !== this.activeChannel.id) return;
        if (this.messages.some(m => m.id === msg.id)) return;
        this.messages.push(msg);
        this.lastMessageId = msg.id;
        this.$nextTick(() => {
          this.scrollToBottom();
          if (msg.type === 'img' && msg.fileId) this.loadImage(msg.fileId);
        });
      };
      sock.on('new_message', this._onNewMessage);

      this._onUserTyping = (data) => {
        if (!this.activeChannel || data.channelId !== this.activeChannel.id) return;
        if (data.userId === this.currentUserId) return;
        const member = (this.channelMembers || []).find((m) => m.id === data.userId);
        const alias = (member && member.chatAlias ? member.chatAlias : '').trim();
        const name = alias || data.userName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Ktoś';
        const isTyping = data.isTyping !== undefined ? data.isTyping : data.typing;
        if (!this._typingTimers) this._typingTimers = {};
        if (this._typingTimers[name]) clearTimeout(this._typingTimers[name]);
        if (isTyping) {
          if (!this.typingUsers.includes(name)) {
            this.typingUsers.push(name);
          }
          this._typingTimers[name] = setTimeout(() => {
            const idx = this.typingUsers.indexOf(name);
            if (idx !== -1) this.typingUsers.splice(idx, 1);
            delete this._typingTimers[name];
          }, 3000);
        } else {
          const idx = this.typingUsers.indexOf(name);
          if (idx !== -1) this.typingUsers.splice(idx, 1);
          delete this._typingTimers[name];
        }
      };
      sock.on('user_typing', this._onUserTyping);

      // Ktoś doczytał kanał — aktualizujemy jego wpis na liście odczytów.
      this._onReadReceipt = (data) => {
        if (!this.activeChannel || data.channelId !== this.activeChannel.id) return;
        const idx = this.channelReads.findIndex((r) => r.userId === data.userId);
        const entry = {
          userId: data.userId,
          firstName: data.firstName,
          lastName: data.lastName,
          lastReadAt: data.lastReadAt,
        };
        if (idx === -1) this.channelReads.push(entry);
        else this.$set(this.channelReads, idx, entry);
      };
      sock.on('read_receipt', this._onReadReceipt);

      // ─── CHANNEL CRUD REAL-TIME EVENTS ───

      this._onChannelCreated = (channel) => {
        if (!this.channels.some(c => c.id === channel.id)) {
          this.channels.push(channel);
        }
      };
      sock.on('channel_created', this._onChannelCreated);

      this._onChannelUpdated = (channel) => {
        const idx = this.channels.findIndex(c => c.id === channel.id);
        if (idx !== -1) {
          this.$set(this.channels, idx, channel);
          if (this.activeChannel && this.activeChannel.id === channel.id) {
            this.activeChannel = channel;
          }
        }
      };
      sock.on('channel_updated', this._onChannelUpdated);

      this._onChannelDeleted = (data) => {
        const idx = this.channels.findIndex(c => c.id === data.id);
        if (idx !== -1) {
          this.channels.splice(idx, 1);
        }
        if (this.activeChannel && this.activeChannel.id === data.id) {
          this.activeChannel = null;
          this.messages = [];
          if (this.channels.length > 0) {
            this.selectChannel(this.channels[0]);
          }
        }
      };
      sock.on('channel_deleted', this._onChannelDeleted);

      this._onSocketError = (err) => {
        console.error('[ChatSocket] error:', err);
        this.socketError = err.message || 'Blad polaczenia WebSocket';
      };
      sock.on('error', this._onSocketError);

      // KLUCZOWE: socket bywa JUŻ połączony (globalny plugin powiadomień łączy go
      // wcześniej), a wtedy zdarzenie 'connect' NIE odpali — więc stan „online"
      // i dołączenie do kanału ustawiamy natychmiast, inaczej czat się nie odświeża.
      if (sock.connected) {
        this.socketConnected = true;
        if (this.activeChannel) {
          sock.emit('join_channel', { channelId: this.activeChannel.id });
        }
      }
    },

    async fetchMe() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('chat/me'), headers: this.authHeaders() });
        this.isChatAdmin = res.data.isChatAdmin;
        this.currentUserId = res.data.id;
        this.currentUser = res.data;
      } catch (e) {
        console.error('Failed to fetch chat user info:', e);
      }
    },

    // ─── CHANNELS ───

    async fetchChannels() {
      this.loadingChannels = true;
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('chat/channels'), headers: this.authHeaders() });
        this.channels = res.data;
      } catch (e) {
        console.error('Failed to fetch channels:', e);
      }
      this.loadingChannels = false;
    },

    async selectChannel(ch) {
      if (!ch) return;
      const requestId = ++this.messageRequestId;
      const sock = getSocket();
      if (this.activeChannel) {
        if (this.messageText || this.attachments.length) {
          this.$set(this.draftsByChannel, this.activeChannel.id, {
            text: this.messageText,
            attachments: this.attachments,
          });
        } else {
          this.$delete(this.draftsByChannel, this.activeChannel.id);
        }
      }
      this.activeChannel = ch;
      this.imageLoadGeneration++;
      Object.values(this.loadedImages).forEach((url) => URL.revokeObjectURL(url));
      this.loadedImages = {};
      this.messages = [];
      this.lastMessageId = null;
      this.typingUsers = [];
      const savedDraft = this.draftsByChannel[ch.id];
      this.messageText = savedDraft?.text || '';
      this.attachments = savedDraft?.attachments || [];
      this.$delete(this.draftsByChannel, ch.id);
      this.headerSearch = '';
      this.messageError = false;
      this.loadingMessages = true;
      this.mobileSidebarOpen = false;
      // Mark channel as read
      markChannelRead(ch.id);
      this.$delete(this.unreadCounts, ch.id);
      if (sock && this.socketConnected) {
        sock.emit('join_channel', { channelId: ch.id });
      }
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('chat/messages/' + ch.id),
          headers: this.authHeaders(),
        });
        if (requestId !== this.messageRequestId) return;
        this.messages = res.data;
        if (this.messages.length > 0) {
          this.lastMessageId = this.messages[this.messages.length - 1].id;
        }
        // Stan odczytów na wejściu — zdarzenia read_receipt dokładają resztę.
        this.fetchChannelReads(ch.id);
        this.$nextTick(() => {
          this.scrollToBottom();
          this.loadVisibleImages();
        });
      } catch (e) {
        if (requestId !== this.messageRequestId) return;
        this.messageError = true;
        console.error('Failed to fetch messages:', e);
      } finally {
        if (requestId === this.messageRequestId) this.loadingMessages = false;
      }
    },

    async pollNewMessages() {
      if (!this.activeChannel || !this.lastMessageId) return;
      const channelId = this.activeChannel.id;
      const requestId = this.messageRequestId;
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('chat/messages/' + channelId + '/new?afterId=' + this.lastMessageId),
          headers: this.authHeaders(),
        });
        if (!this.activeChannel || this.activeChannel.id !== channelId || requestId !== this.messageRequestId) return;
        if (res.data.length > 0) {
          const existingIds = new Set(this.messages.map(m => m.id));
          const newMsgs = res.data.filter(m => !existingIds.has(m.id));
          if (newMsgs.length > 0) {
            this.messages.push(...newMsgs);
            this.lastMessageId = this.messages[this.messages.length - 1].id;
            this.$nextTick(() => {
              this.scrollToBottom();
              this.loadVisibleImages();
            });
          }
        }
      } catch (e) {
        // silent
      }
    },

    openChannelDialog(ch) {
      if (ch) {
        this.editingChannel = ch;
        this.channelForm = { name: ch.name, description: ch.description || '', type: ch.type || 'public', color: ch.color || '#1976D2', icon: ch.icon || '#' };
        if (ch.type === 'private') {
          this.loadChannelMembers(ch.id);
          this.loadCompanyAdmins();
        }
      } else {
        this.editingChannel = null;
        this.channelForm = { name: '', description: '', type: 'public', color: '#1976D2', icon: '#' };
        this.channelMembers = [];
        this.addMemberPick = null;
      }
      this.channelDialog = true;
    },

    async loadChannelMembers(channelId) {
      this.loadingMembers = true;
      try {
        const res = await axios.get(this.apiUrl(`chat/channels/${channelId}/members`), { headers: this.authHeaders() });
        this.channelMembers = res.data || [];
      } catch (e) { this.channelMembers = []; }
      this.loadingMembers = false;
    },
    async loadCompanyAdmins() {
      if (this.companyAdmins.length) return;
      try {
        const res = await axios.get(this.apiUrl('company-admins'), { headers: this.authHeaders() });
        const list = res.data?.data || res.data || [];
        this.companyAdmins = Array.isArray(list) ? list : (list.result || []);
      } catch (e) { this.companyAdmins = []; }
    },
    async onAddMemberPicked(userId) {
      if (!userId || !this.editingChannel) return;
      try {
        await axios.post(this.apiUrl(`chat/channels/${this.editingChannel.id}/members`), { userId }, { headers: this.authHeaders() });
        await this.loadChannelMembers(this.editingChannel.id);
      } catch (e) { /* ignore */ }
      this.addMemberPick = null;
    },
    async removeMember(userId) {
      if (!this.editingChannel) return;
      try {
        await axios.delete(this.apiUrl(`chat/channels/${this.editingChannel.id}/members/${userId}`), { headers: this.authHeaders() });
        await this.loadChannelMembers(this.editingChannel.id);
      } catch (e) { /* ignore */ }
    },

    async saveChannel() {
      this.saving = true;
      try {
        const iconMap = { public: '#', private: '🔒', alert: '🚨', archive: '📦' };
        // Ikona: wybrana przez admina (paleta lub własny emoji); gdy pusta —
        // domyślna z typu kanału.
        const chosenIcon = (this.channelForm.icon || '').trim() || iconMap[this.channelForm.type] || '#';
        if (this.editingChannel) {
          await axios({
            method: 'put',
            url: this.apiUrl('chat/channels/' + this.editingChannel.id),
            data: { name: this.channelForm.name, description: this.channelForm.description, type: this.channelForm.type, icon: chosenIcon, color: this.channelForm.color },
            headers: this.authHeaders(),
          });
        } else {
          if (!this.channelForm.name || !this.channelForm.name.trim()) { this.saving = false; return; }
          await axios({
            method: 'post',
            url: this.apiUrl('chat/channels'),
            data: { ...this.channelForm, icon: chosenIcon, color: this.channelForm.color },
            headers: this.authHeaders(),
          });
        }
        this.channelDialog = false;
        await this.fetchChannels();
      } catch (e) {
        console.error('Failed to save channel:', e);
      }
      this.saving = false;
    },

    confirmDeleteChannel(ch) {
      this.deleteTarget = ch;
      this.deleteDialog = true;
    },

    async executeDeleteChannel() {
      try {
        await axios({
          method: 'delete',
          url: this.apiUrl('chat/channels/' + this.deleteTarget.id),
          headers: this.authHeaders(),
        });
        this.deleteDialog = false;
        if (this.activeChannel && this.activeChannel.id === this.deleteTarget.id) {
          this.activeChannel = null;
          this.messages = [];
        }
        await this.fetchChannels();
        if (!this.activeChannel && this.channels.length > 0) {
          this.selectChannel(this.channels[0]);
        }
      } catch (e) {
        console.error('Failed to delete channel:', e);
      }
    },

    // ─── MESSAGES ───

    async sendMessage() {
      const text = this.messageText.trim();
      if (!text && !this.attachments.length) return;
      if (!this.activeChannel || this.sending) return;
      this.sending = true;
      const channelId = this.activeChannel.id;
      const attachmentsToSend = [...this.attachments];

      const sock = getSocket();

      if (text) {
        if (sock && this.socketConnected) {
          sock.emit('send_message', { text, channelId });
          sock.emit('typing_stop', { channelId });
          if (this.typingStopTimeout) clearTimeout(this.typingStopTimeout);
        } else {
          try {
            const res = await axios({
              method: 'post',
              url: this.apiUrl('chat/messages'),
              data: { text, channelId },
              headers: this.authHeaders(),
            });
            if (res.data && !this.messages.some((message) => message.id === res.data.id)) {
              this.messages.push(res.data);
              this.lastMessageId = res.data.id;
            }
          } catch (e) {
            console.error('Failed to send message:', e);
          }
        }
      }

      for (const att of attachmentsToSend) {
        try {
          const formData = new FormData();
          formData.append('file', att.file);
          formData.append('channelId', channelId);
          const res = await axios({
            method: 'post',
            url: this.apiUrl('chat/messages/file'),
            data: formData,
            headers: this.authHeaders(),
          });
          if (!this.socketConnected) {
            this.messages.push(res.data);
            this.lastMessageId = res.data.id;
          }
        } catch (e) {
          console.error('Failed to upload file:', e);
        }
      }

      attachmentsToSend.forEach((att) => { if (att.url) URL.revokeObjectURL(att.url); });
      this.$delete(this.draftsByChannel, channelId);
      if (this.activeChannel && this.activeChannel.id === channelId) {
        this.messageText = '';
        this.attachments = [];
      }
      this.sending = false;
      this.$nextTick(() => {
        this.scrollToBottom();
        this.focusComposer();
      });
    },

    // ─── ATTACHMENTS ───

    attachFiles(event) {
      for (const f of Array.from(event.target.files)) {
        this.attachments.push({ type: 'file', file: f, name: f.name, ext: f.name.split('.').pop().toUpperCase() });
      }
      event.target.value = '';
    },

    attachImages(event) {
      for (const f of Array.from(event.target.files)) {
        this.attachments.push({ type: 'img', file: f, name: f.name, url: URL.createObjectURL(f), ext: 'IMG' });
      }
      event.target.value = '';
    },

    insertTemplate() {
      this.messageText = TEMPLATES[this.templateIndex % TEMPLATES.length];
      this.templateIndex++;
      this.$nextTick(this.focusComposer);
    },

    removeAttachment(index) {
      const [attachment] = this.attachments.splice(index, 1);
      if (attachment && attachment.url) URL.revokeObjectURL(attachment.url);
    },

    focusComposer() {
      const input = this.$refs.chatInput;
      if (input) input.focus();
    },

    // ─── TYPING ───

    onTextInput() {
      const sock = getSocket();
      if (!sock || !this.socketConnected || !this.activeChannel) return;
      sock.emit('typing_start', { channelId: this.activeChannel.id });
      if (this.typingStopTimeout) clearTimeout(this.typingStopTimeout);
      this.typingStopTimeout = setTimeout(() => {
        sock.emit('typing_stop', { channelId: this.activeChannel.id });
      }, 2000);
    },

    // ─── HELPERS ───

    isNewGroup(i, list = this.messages) {
      if (i === 0) return true;
      const prev = list[i - 1];
      const curr = list[i];
      if (prev.type === 'system' || curr.type === 'system') return true;
      const elapsed = new Date(curr.createdAt).getTime() - new Date(prev.createdAt).getTime();
      return prev.authorId !== curr.authorId || elapsed > 5 * 60 * 1000;
    },

    showDateSeparator(i, list = this.messages) {
      if (i === 0) return true;
      const previous = new Date(list[i - 1].createdAt).toDateString();
      const current = new Date(list[i].createdAt).toDateString();
      return previous !== current;
    },

    formatDateLabel(dateStr) {
      const date = new Date(dateStr);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return 'Dzisiaj';
      if (date.toDateString() === yesterday.toDateString()) return 'Wczoraj';
      return new Intl.DateTimeFormat('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
    },

    // Indeks ostatniej własnej wiadomości na wyświetlanej liście.
    isLastOwnMessage(i) {
      const list = this.displayedMessages || [];
      for (let k = list.length - 1; k >= 0; k--) {
        if (this.isOwnMessage(list[k])) return k === i;
      }
      return false;
    },
    // Kto widział tę wiadomość: doczytał do momentu późniejszego niż jej wysłanie.
    // Siebie pomijamy — własnej wiadomości nie „odczytujemy".
    readersFor(msg) {
      const sentAt = new Date(msg.createdAt).getTime();
      return (this.channelReads || []).filter(
        (r) => r.userId !== this.currentUserId && new Date(r.lastReadAt).getTime() >= sentAt,
      );
    },
    readsLabel(msg) {
      const readers = this.readersFor(msg);
      if (!readers.length) return 'Wysłano';
      const names = readers.map((r) => {
        const alias = (r.chatAlias || '').trim();
        return alias || `${r.firstName || ''} ${(r.lastName || '').charAt(0)}.`.trim();
      });
      if (names.length <= 3) return 'Przeczytali: ' + names.join(', ');
      return `Przeczytali: ${names.slice(0, 3).join(', ')} i ${names.length - 3} in.`;
    },
    async fetchChannelReads(channelId) {
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('chat/channels/' + channelId + '/reads'),
          headers: this.authHeaders(),
        });
        this.channelReads = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.channelReads = [];
      }
    },
    isOwnMessage(msg) {
      return Boolean(msg && msg.authorId && this.currentUserId && msg.authorId === this.currentUserId);
    },

    getAuthorName(msg) {
      // Alias („KJ") wygrywa z imieniem i nazwiskiem — ustawia go każdy sam
      // w Ustawieniach konta.
      if (msg.author) {
        const alias = (msg.author.chatAlias || '').trim();
        if (alias) return alias;
        return `${msg.author.firstName || ''} ${msg.author.lastName || ''}`.trim() || 'Użytkownik';
      }
      return 'System';
    },

    getAuthorInitials(msg) {
      if (msg.author) {
        const alias = (msg.author.chatAlias || '').trim();
        // Alias bywa już inicjałami („KJ") — bierzemy z niego dwa pierwsze znaki.
        if (alias) return alias.replace(/\s+/g, '').slice(0, 2).toUpperCase();
        const first = msg.author.firstName || 'U';
        const last = msg.author.lastName || '';
        return (first[0] + (last[0] || '')).toUpperCase();
      }
      return 'SY';
    },

    // Zdjęcie profilowe autora z mapy (id → avatar). Brak = inicjały.
    authorAvatar(msg) {
      if (!msg || !msg.authorId) return null;
      return this.avatarMap[msg.authorId] || null;
    },

    getAuthorColor(msg) {
      if (!msg.authorId) return AVATAR_COLORS[0];
      let hash = 0;
      for (let i = 0; i < msg.authorId.length; i++) {
        hash = msg.authorId.charCodeAt(i) + ((hash << 5) - hash);
      }
      return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    },

    formatTime(dateStr) {
      const d = new Date(dateStr);
      return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
    },

    async downloadFile(fileId, fileName) {
      if (!fileId) return;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl('aws/download-file/' + fileId),
          headers: this.authHeaders(),
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'file';
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Failed to download file:', e);
      }
    },

    async loadImage(fileId) {
      if (!fileId || this.loadedImages[fileId]) return;
      const generation = this.imageLoadGeneration;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl('aws/download-file/' + fileId),
          headers: this.authHeaders(),
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        if (generation !== this.imageLoadGeneration) {
          URL.revokeObjectURL(url);
          return;
        }
        this.$set(this.loadedImages, fileId, url);
      } catch (e) {
        console.error('Failed to load image:', e);
      }
    },

    loadVisibleImages() {
      this.messages.forEach(msg => {
        if (msg.type === 'img' && msg.fileId && !this.loadedImages[msg.fileId]) {
          this.loadImage(msg.fileId);
        }
      });
    },

    formatText(text) {
      if (!text) return '';
      return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
    },

    scrollToBottom() {
      const el = this.$refs.messagesArea;
      if (el) el.scrollTop = el.scrollHeight;
    },

    playNotificationSound() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1047, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.25);
      } catch (e) {
        // Audio not available
      }
    },
  },
};
</script>

<style scoped>
/* ─── Shell ─── */
.chat-page {
  overflow: hidden;
  background: #0D1117;
}

/* ===== Channel dialog (nowoczesny) ===== */
.chdlg {
  background: #131c28;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.chdlg__header {
  background: linear-gradient(135deg, #2C5FF6 0%, #5D5FEF 60%, #9333EA 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.chdlg__header-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.chdlg__header-text { flex: 1; min-width: 0; color: #fff; }
.chdlg__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1.2;
}
.chdlg__sub {
  font-size: 11.5px;
  opacity: 0.78;
  margin-top: 2px;
}
.chdlg__close { color: #fff !important; }

.chdlg__body { padding: 20px; }
.chdlg__group { margin-bottom: 16px; }
.chdlg__group:last-child { margin-bottom: 0; }
.chdlg__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: #94A0B8;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.chdlg__input >>> .v-input__slot {
  background: #0F1923 !important;
  border-radius: 10px !important;
}
.chdlg__input >>> input,
.chdlg__input >>> textarea {
  color: #E6EAF2 !important;
  font-size: 13px;
}
.chdlg__input >>> .v-icon {
  color: #94A0B8 !important;
}

/* Type kafelki */
.chdlg__types {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.chdlg__type {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #0F1923;
  border: 1px solid #1F2A3A;
  color: #94A0B8;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s ease;
  text-align: left;
}
.chdlg__type:hover {
  background: #1A2535;
  border-color: #2C5FF6;
  color: #E6EAF2;
}
.chdlg__type.active {
  background: linear-gradient(135deg, rgba(44, 95, 246, 0.22), rgba(147, 51, 234, 0.22));
  border-color: #2C5FF6;
  color: #fff;
  box-shadow: 0 0 0 1px #2C5FF6 inset;
}
.chdlg__type-icon { font-size: 15px; }

/* Color picker */
.chdlg__colors {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chdlg__color {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  padding: 0;
}
.chdlg__color:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}
.chdlg__color.active {
  transform: scale(1.1);
  box-shadow: 0 0 0 2px #131c28, 0 0 0 4px #fff;
}
.chdlg__icons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chdlg__icon-opt {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #0F1923;
  color: #e2e8f0;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  padding: 0;
}
.chdlg__icon-opt:hover {
  transform: scale(1.1);
  border-color: #60a5fa;
}
.chdlg__icon-opt.active {
  border-color: #60a5fa;
  background: rgba(96, 165, 250, 0.18);
  box-shadow: 0 0 0 1px #60a5fa;
}
.chdlg__members {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  background: #0F1923;
  border-radius: 10px;
  min-height: 44px;
}
.chdlg__member {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 10px;
  background: rgba(44, 95, 246, 0.16);
  border: 1px solid rgba(44, 95, 246, 0.32);
  color: #E6EAF2;
  font-size: 12px;
  border-radius: 999px;
}
.chdlg__member-name { font-weight: 500; }
.chdlg__member-remove {
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border-radius: 50%;
}
.chdlg__member-remove:hover { background: rgba(239, 83, 80, 0.16); }

/* Footer */
.chdlg__footer {
  padding: 14px 20px 18px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #1F2A3A;
  background: rgba(0, 0, 0, 0.18);
}
.chdlg__btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  border: none;
  transition: all 0.18s ease;
}
.chdlg__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.chdlg__btn--ghost {
  background: transparent;
  color: #94A0B8;
}
.chdlg__btn--ghost:hover:not(:disabled) {
  background: #1A2535;
  color: #E6EAF2;
}
.chdlg__btn--primary {
  background: linear-gradient(135deg, #2C5FF6 0%, #5D5FEF 100%);
  color: #fff;
  box-shadow: 0 6px 16px rgba(44, 95, 246, 0.35);
}
.chdlg__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(44, 95, 246, 0.5);
}
.chdlg__btn--primary:active:not(:disabled) {
  transform: translateY(0);
}
.btp-chat {
  background: #0D1117;
  color: #dfe2e9;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.btp-chat__shell {
  display: flex;
  height: 100%;
}
.min-width-0 { min-width: 0; }

/* ─── Sidebar ─── */
.btp-chat__sidebar {
  width: 260px;
  min-width: 260px;
  background: #1A2433;
  border-right: 1px solid rgba(255,255,255,0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
}
.btp-chat__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.btp-chat__brand-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 8px rgba(59,130,246,0.35);
}
.btp-chat__brand-title {
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: -0.01em;
}
.btp-chat__brand-subtitle {
  color: #64748b;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-top: 2px;
}
.btp-chat__new-btn {
  width: 100%;
  padding: 7px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex; align-items: center; justify-content: center;
  gap: 4px;
  transition: opacity 0.15s;
  border: none;
  cursor: pointer;
}
.btp-chat__new-btn:hover { opacity: 0.9; }

/* Nav */
.btp-chat__nav {
  flex: 1;
  overflow-y: auto;
  padding: 4px 8px 12px;
}
.btp-chat__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px 4px;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.btp-chat__section-add {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.btp-chat__section-add:hover { opacity: 1; }

/* Channel row */
.btp-chat__channel {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  color: #94a3b8;
  font-size: 13px;
  text-decoration: none;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
  position: relative;
  margin-bottom: 3px;
}
.btp-chat__channel:hover {
  background: rgba(255,255,255,0.06);
  color: #e2e8f0;
  transform: translateX(2px);
}
.btp-chat__channel.is-active {
  background: linear-gradient(135deg, rgba(44,95,246,0.18), rgba(147,51,234,0.12));
  color: #fff;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #2C5FF6;
}
.btp-chat__channel.has-unread .btp-chat__channel-name {
  color: #fff;
  font-weight: 600;
}
.btp-chat__channel-icon {
  font-size: 15px;
  font-weight: 600;
  flex-shrink: 0;
  width: 16px;
  text-align: center;
}
.btp-chat__channel-name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btp-chat__unread-pill {
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
  min-width: 18px;
  text-align: center;
  line-height: 1.4;
}
.btp-chat__channel-menu {
  opacity: 0;
  transition: opacity 0.15s;
}
.btp-chat__channel:hover .btp-chat__channel-menu {
  opacity: 1;
}

/* User footer */
.btp-chat__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-top: 1px solid rgba(255,255,255,0.05);
  background: #0F1B2A;
}
.btp-chat__user-avatar {
  position: relative;
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #42A5F5, #1976d2);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.btp-chat__user-dot {
  position: absolute;
  bottom: -2px; right: -2px;
  width: 10px; height: 10px;
  border-radius: 50%;
  background: #4ade80;
  border: 2px solid #0F1B2A;
}
.btp-chat__user-name {
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btp-chat__user-chip {
  background: #fbbf24;
  color: #1c1917;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 3px;
}
.btp-chat__user-status {
  color: #64748b;
  font-size: 11px;
  margin-top: 1px;
}

/* ─── Main ─── */
.btp-chat__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0D1117;
  min-width: 0;
  height: 100%;
}
.btp-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 52px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  background: rgba(13,17,23,0.85);
  backdrop-filter: blur(8px);
  position: sticky; top: 0;
  z-index: 5;
}
.btp-chat__header-name {
  color: #fff;
  font-weight: 700;
  font-size: 15px;
}
.btp-chat__header-divider {
  width: 1px;
  height: 16px;
  background: rgba(255,255,255,0.1);
  margin: 0 4px;
}
.btp-chat__header-desc {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btp-chat__search {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #181d25;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 999px;
  padding: 5px 12px;
  width: 220px;
  transition: border-color 0.15s;
}
.btp-chat__search:focus-within {
  border-color: #3b82f6;
}
.btp-chat__search input {
  background: transparent;
  border: none;
  outline: none;
  color: #e2e8f0;
  font-size: 12px;
  flex: 1;
  min-width: 0;
}
.btp-chat__search input::placeholder { color: #475569; }
.btp-chat__icon-btn {
  color: #94a3b8;
  background: transparent;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}
.btp-chat__icon-btn:hover {
  color: #fff;
  background: rgba(255,255,255,0.05);
}

/* ─── Messages ─── */
.btp-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

/* Empty state */
.btp-chat__empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}
.btp-chat__empty-halo {
  position: relative;
  width: 128px; height: 128px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(59,130,246,0.25), transparent 70%);
  border: 1px solid rgba(255,255,255,0.05);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
  margin-bottom: 20px;
}
.btp-chat__empty-badge {
  position: absolute;
  top: -4px; right: -4px;
  width: 32px; height: 32px;
  border-radius: 50%;
  background: #1c2634;
  border: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
}
.btp-chat__empty-title {
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
}
.btp-chat__empty-text {
  color: #64748b;
  font-size: 13px;
  line-height: 1.55;
  max-width: 420px;
  margin: 0 0 24px;
}
.btp-chat__empty-text strong {
  color: #cbd5e1;
  font-weight: 600;
}
.btp-chat__empty-cta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}
.btp-chat__btn {
  padding: 8px 22px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
}
.btp-chat__btn--primary {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  color: #0b1220;
}
.btp-chat__btn--primary:hover { opacity: 0.9; }
.btp-chat__btn--outline {
  background: transparent;
  border-color: rgba(255,255,255,0.15);
  color: #cbd5e1;
}
.btp-chat__btn--outline:hover {
  background: rgba(255,255,255,0.05);
  color: #fff;
}

/* Messages */
.btp-chat__system {
  display: flex;
  justify-content: center;
  margin: 12px 0;
}
.btp-chat__system span {
  background: rgba(30,41,59,0.4);
  border: 1px solid rgba(255,255,255,0.05);
  padding: 3px 14px;
  border-radius: 999px;
  color: #64748b;
  font-size: 11px;
  font-style: italic;
}
.btp-chat__msg {
  display: flex;
  gap: 12px;
  padding: 2px 0;
  border-radius: 4px;
  transition: background 0.15s;
  margin: 0 -8px;
  padding: 2px 8px;
}
.btp-chat__msg:hover { background: rgba(255,255,255,0.02); }
.btp-chat__msg.is-new-group { margin-top: 14px; }
.btp-chat__msg-avatar {
  width: 40px; height: 40px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  overflow: hidden;
}
.btp-chat__msg-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.btp-chat__msg-avatar-spacer {
  width: 40px;
  flex-shrink: 0;
}
.btp-chat__msg-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
}
.btp-chat__msg-author {
  color: #fff;
  font-weight: 700;
  font-size: 14px;
}
.btp-chat__msg-time {
  color: #475569;
  font-size: 11px;
  font-weight: 400;
}
.btp-chat__msg-text {
  color: rgba(255,255,255,0.88);
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}
.btp-chat__msg-img {
  max-width: 320px;
  max-height: 220px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.06);
  display: block;
  cursor: pointer;
  margin-top: 4px;
}
.btp-chat__msg-img-placeholder {
  width: 240px; height: 120px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(30,41,59,0.3);
  display: flex; align-items: center; justify-content: center;
  margin-top: 4px;
}
.btp-chat__msg-file {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 360px;
  padding: 10px 12px;
  background: #181d25;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
  margin-top: 4px;
}
.btp-chat__msg-file:hover { border-color: #3b82f6; }
.btp-chat__msg-file-icon {
  background: rgba(248,113,113,0.15);
  padding: 8px;
  border-radius: 6px;
}
.btp-chat__msg-file-name {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btp-chat__msg-file-meta {
  color: #64748b;
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 700;
  margin-top: 2px;
}

/* Typing */
.btp-chat__reads {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 11px;
  color: #64748b;
}
.btp-chat__typing {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  color: #64748b;
  font-size: 12px;
  font-style: italic;
}
.typing-dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.typing-dots span {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: #60a5fa;
  animation: typing-bounce 1.4s infinite ease-in-out both;
}
.typing-dots span:nth-child(1) { animation-delay: 0s; }
.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

/* ─── Footer / Input ─── */
.btp-chat__footer {
  padding: 12px 20px 14px;
}
.btp-chat__attach-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.btp-chat__attach {
  position: relative;
  width: 56px; height: 56px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
}
.btp-chat__attach img {
  width: 100%; height: 100%;
  object-fit: cover;
}
.btp-chat__attach-file {
  width: 100%; height: 100%;
  background: #1e293b;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 2px;
  color: #94a3b8;
  font-size: 9px;
  font-weight: 700;
}
.btp-chat__attach-remove {
  position: absolute;
  top: 2px; right: 2px;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: rgba(15,23,42,0.9);
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.btp-chat__input-box {
  background: #181d25;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.15s;
}
.btp-chat__input-box:focus-within {
  border-color: #3b82f6;
}
.btp-chat__input-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 10px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  background: rgba(15,27,42,0.4);
}
.btp-chat__tool-btn {
  color: #94a3b8;
  background: transparent;
  border: none;
  padding: 5px 7px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btp-chat__tool-btn:hover {
  background: rgba(255,255,255,0.06);
  color: #fff;
}
.btp-chat__tool-sep {
  width: 1px;
  height: 16px;
  background: rgba(255,255,255,0.08);
  margin: 0 4px;
}
.btp-chat__input-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 8px 10px;
}
.btp-chat__input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  color: #e2e8f0;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 4px;
  max-height: 120px;
  min-height: 28px;
}
.btp-chat__input::placeholder { color: #475569; }
.btp-chat__input-mic {
  color: #94a3b8;
  background: transparent;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}
.btp-chat__input-mic:hover {
  color: #fff;
  background: rgba(255,255,255,0.06);
}
.btp-chat__input-send {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border: none;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.btp-chat__input-send:hover:not(:disabled) { transform: scale(1.04); }
.btp-chat__input-send:active:not(:disabled) { transform: scale(0.96); }
.btp-chat__input-send:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btp-chat__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px 0;
  color: #475569;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.btp-chat__status-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
}
</style>

<style scoped>
/* ─── BTP Conversations 2026 ─────────────────────────────────────────────── */
.chat-page {
  --chat-bg: #171b27;
  --chat-panel: #222838;
  --chat-panel-deep: #1c2231;
  --chat-panel-hover: #292f40;
  --chat-border: rgba(255, 255, 255, 0.075);
  --chat-border-strong: rgba(142, 167, 255, 0.32);
  --chat-text: #f5f7fc;
  --chat-muted: #aeb8ca;
  --chat-subtle: #8e99ad;
  --chat-primary: #5b7cff;
  --chat-focus: #8ea7ff;
  --chat-success: #42d787;
  --chat-danger: #ff7373;
  height: calc(100vh - 56px);
  height: calc(100dvh - 56px);
  width: calc(100% + 48px);
  max-width: none;
  margin: -24px;
  overflow: hidden;
  color: var(--chat-text);
  background:
    radial-gradient(circle at 82% -12%, rgba(91, 124, 255, 0.16), transparent 34%),
    radial-gradient(circle at 18% 110%, rgba(167, 139, 250, 0.09), transparent 32%),
    var(--chat-bg);
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.btp-chat__workspace {
  height: 100%;
  flex-wrap: nowrap !important;
  overflow: hidden;
}

.btp-chat__sidebar {
  position: relative;
  z-index: 12;
  flex: 0 0 310px;
  width: 310px;
  max-width: 310px;
  height: 100%;
  border-right: 1px solid var(--chat-border);
  background: rgba(28, 34, 49, 0.94);
  box-shadow: 18px 0 50px rgba(4, 7, 14, 0.12);
  backdrop-filter: blur(20px);
}

.btp-chat__sidebar-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.btp-chat__brand {
  min-height: 82px;
  padding: 17px 18px;
  gap: 12px;
  border-bottom: 1px solid var(--chat-border);
  background: linear-gradient(150deg, rgba(91, 124, 255, 0.13), rgba(34, 40, 56, 0) 62%);
}

.btp-chat__brand-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(145deg, #6d89ff, #4d6bf1 58%, #7c5ce7);
  box-shadow: 0 10px 26px rgba(91, 124, 255, 0.3), inset 0 1px rgba(255, 255, 255, 0.25);
}

.btp-chat__brand-copy {
  min-width: 0;
  flex: 1;
}

.btp-chat__brand-kicker {
  margin-bottom: 3px;
  color: #9dacff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.13em;
  line-height: 1.2;
  text-transform: uppercase;
}

.btp-chat__brand-title {
  overflow: hidden;
  color: var(--chat-text);
  font-size: 15px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btp-chat__sidebar-close,
.btp-chat__mobile-menu,
.btp-chat__sidebar-backdrop {
  display: none;
}

.btp-chat__sidebar-actions {
  display: grid;
  gap: 10px;
  padding: 15px 14px 10px;
}

.btp-chat__channel-search,
.btp-chat__search {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 42px;
  padding: 0 12px;
  color: var(--chat-subtle);
  background: rgba(12, 16, 26, 0.34);
  border: 1px solid var(--chat-border);
  border-radius: 13px;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.btp-chat__channel-search:focus-within,
.btp-chat__search:focus-within {
  background: rgba(15, 20, 32, 0.65);
  border-color: var(--chat-focus);
  box-shadow: 0 0 0 3px rgba(142, 167, 255, 0.12);
}

.btp-chat__channel-search input,
.btp-chat__search input {
  min-width: 0;
  flex: 1;
  color: var(--chat-text);
  background: transparent;
  border: 0;
  outline: 0;
  font: inherit;
  font-size: 13px;
}

.btp-chat__channel-search input::placeholder,
.btp-chat__search input::placeholder { color: #808ba0; }

.btp-chat__channel-search button,
.btp-chat__search button {
  display: inline-flex;
  padding: 0;
  color: var(--chat-subtle);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.btp-chat__new-btn {
  min-height: 44px;
  padding: 6px 10px 6px 7px;
  gap: 9px;
  justify-content: flex-start;
  border-radius: 13px;
  background: linear-gradient(135deg, #5b7cff, #695de7);
  box-shadow: 0 9px 24px rgba(91, 124, 255, 0.24);
}

.btp-chat__new-btn > span:nth-child(2) { flex: 1; text-align: left; }

.btp-chat__new-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 31px;
  height: 31px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.14);
}

.btp-chat__new-btn:hover {
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(91, 124, 255, 0.34);
}

.btp-chat__nav {
  min-height: 0;
  padding: 4px 10px 16px;
  scrollbar-color: rgba(142, 167, 255, 0.24) transparent;
}

.btp-chat__section-head {
  padding: 13px 10px 7px;
  color: #939eb2;
  font-size: 10px;
  letter-spacing: 0.11em;
}

.btp-chat__section-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  color: var(--chat-muted) !important;
}

.btp-chat__section-add:hover { background: rgba(255, 255, 255, 0.07); }

.btp-chat__channel {
  min-height: 52px;
  margin-bottom: 5px;
  padding: 8px 9px;
  gap: 10px;
  color: var(--chat-muted);
  border: 1px solid transparent;
  border-radius: 14px;
}

.btp-chat__channel-select {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
  padding: 0;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.btp-chat__channel::before {
  content: '';
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: transparent;
}

.btp-chat__channel:hover {
  color: var(--chat-text);
  background: var(--chat-panel-hover);
  border-color: var(--chat-border);
  transform: none;
}

.btp-chat__channel.is-active {
  color: var(--chat-text);
  background: linear-gradient(105deg, rgba(91, 124, 255, 0.21), rgba(167, 139, 250, 0.08));
  border-color: rgba(124, 148, 255, 0.24);
  box-shadow: 0 8px 22px rgba(8, 12, 23, 0.16);
}

.btp-chat__channel.is-active::before { background: #7c94ff; }

.btp-chat__channel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.055);
}

.btp-chat__channel-name { font-size: 13px; font-weight: 650; }

.btp-chat__unread-pill {
  min-width: 21px;
  padding: 2px 6px;
  background: linear-gradient(135deg, #ff7373, #e94d72);
  box-shadow: 0 4px 12px rgba(255, 115, 115, 0.25);
}

.btp-chat__channel-menu { margin-left: -5px; }
.btp-chat__channel.is-active .btp-chat__channel-menu { opacity: 0.8; }

.btp-chat__nav-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 32px 12px;
  color: var(--chat-subtle);
  font-size: 12px;
}

.btp-chat__user {
  min-height: 68px;
  padding: 12px 15px;
  border-color: var(--chat-border);
  background: rgba(17, 22, 34, 0.56);
}

.btp-chat__user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 13px;
  background: linear-gradient(145deg, #8096ff, #5b62d9);
  box-shadow: 0 7px 18px rgba(91, 124, 255, 0.2);
}

.btp-chat__user-name { color: var(--chat-text); }
.btp-chat__user-chip { border-radius: 6px; background: #ffca68; }
.btp-chat__user-status { display: flex; align-items: center; gap: 5px; color: var(--chat-subtle); }

.btp-chat__connection-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #778195;
}

.btp-chat__connection-dot.online {
  background: var(--chat-success);
  box-shadow: 0 0 0 3px rgba(66, 215, 135, 0.1);
}

.btp-chat__main {
  position: relative;
  flex: 1 1 auto;
  max-width: none;
  height: 100%;
  background: transparent;
}

.btp-chat__header {
  position: relative;
  z-index: 8;
  min-height: 72px;
  height: 72px;
  padding: 0 22px;
  gap: 16px;
  background: rgba(23, 27, 39, 0.8);
  border-color: var(--chat-border);
  backdrop-filter: blur(22px);
}

.btp-chat__header-channel {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
  flex: 1;
}

.btp-chat__header-channel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 13px;
  color: #7c94ff;
  background: rgba(91, 124, 255, 0.12);
  border: 1px solid rgba(124, 148, 255, 0.17);
  font-size: 18px;
  font-weight: 800;
}

.btp-chat__header-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.btp-chat__header-name {
  overflow: hidden;
  color: var(--chat-text);
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btp-chat__header-desc {
  overflow: hidden;
  max-width: 440px;
  margin-top: 2px;
  color: var(--chat-subtle);
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btp-chat__header-actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

.btp-chat__search { width: 250px; min-height: 40px; }
.btp-chat__search.is-disabled { opacity: 0.58; }

.btp-chat__connection {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 0 10px;
  color: var(--chat-subtle);
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid var(--chat-border);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.btp-chat__connection > span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #778195;
}

.btp-chat__connection.online { color: #93e8ba; }
.btp-chat__connection.online > span { background: var(--chat-success); box-shadow: 0 0 0 3px rgba(66, 215, 135, 0.1); }

.btp-chat__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  color: var(--chat-muted);
  border: 1px solid var(--chat-border);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.035);
}

.btp-chat__icon-btn:hover { color: var(--chat-text); background: var(--chat-panel-hover); border-color: var(--chat-border-strong); }

.btp-chat__messages {
  min-height: 0;
  padding: 26px max(24px, calc((100% - 940px) / 2));
  scrollbar-color: rgba(142, 167, 255, 0.25) transparent;
}

.btp-chat__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  height: 100%;
  color: var(--chat-muted);
  font-size: 13px;
}

.btp-chat__loading-orbit {
  display: inline-flex;
  padding: 18px;
  border: 1px solid var(--chat-border);
  border-radius: 22px;
  background: var(--chat-panel-deep);
  box-shadow: 0 18px 44px rgba(5, 8, 16, 0.22);
}

.btp-chat__empty { min-height: 100%; padding: 40px 20px; }

.btp-chat__empty-halo {
  width: 116px;
  height: 116px;
  margin-bottom: 23px;
  color: #8ba2ff;
  background:
    radial-gradient(circle at 35% 28%, rgba(142, 167, 255, 0.28), transparent 52%),
    linear-gradient(145deg, rgba(91, 124, 255, 0.12), rgba(167, 139, 250, 0.07));
  border: 1px solid rgba(142, 167, 255, 0.18);
  box-shadow: 0 24px 65px rgba(5, 8, 16, 0.3), inset 0 1px rgba(255, 255, 255, 0.08);
}

.btp-chat__empty-halo--small { width: 88px; height: 88px; }
.btp-chat__empty-halo--error { color: var(--chat-danger); background: rgba(255, 115, 115, 0.08); border-color: rgba(255, 115, 115, 0.2); }

.btp-chat__empty-kicker {
  margin-bottom: 8px;
  color: #91a5ff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.btp-chat__empty-title { color: var(--chat-text); font-size: clamp(21px, 2vw, 27px); letter-spacing: -0.025em; }
.btp-chat__empty-text { max-width: 480px; color: var(--chat-muted); font-size: 13px; line-height: 1.7; }
.btp-chat__empty-mobile-action { display: none; }

.btp-chat__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 8px 17px;
  border-radius: 12px;
}

.btp-chat__btn--primary {
  color: #fff;
  background: linear-gradient(135deg, #5b7cff, #695de7);
  box-shadow: 0 9px 24px rgba(91, 124, 255, 0.25);
}

.btp-chat__btn--outline { color: var(--chat-muted); border-color: var(--chat-border-strong); }

.btp-chat__date-separator {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 21px 0 12px;
  color: var(--chat-subtle);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.btp-chat__date-separator::before,
.btp-chat__date-separator::after { content: ''; height: 1px; flex: 1; background: var(--chat-border); }

.btp-chat__date-separator span {
  padding: 5px 11px;
  border: 1px solid var(--chat-border);
  border-radius: 999px;
  background: rgba(28, 34, 49, 0.72);
}

.btp-chat__system { margin: 11px 0; }
.btp-chat__system span { color: var(--chat-subtle); background: rgba(28, 34, 49, 0.68); border-color: var(--chat-border); font-style: normal; }

.btp-chat__msg {
  width: fit-content;
  min-width: min(280px, 70%);
  max-width: 76%;
  margin: 3px 0;
  padding: 0;
  gap: 10px;
  border-radius: 18px;
}

.btp-chat__msg:hover { background: transparent; }
.btp-chat__msg.is-new-group { margin-top: 15px; }
.btp-chat__msg.is-own { flex-direction: row-reverse; margin-left: auto; }

.btp-chat__msg > div:last-child {
  padding: 10px 13px 11px;
  background: var(--chat-panel-deep);
  border: 1px solid var(--chat-border);
  border-radius: 5px 18px 18px 18px;
  box-shadow: 0 8px 24px rgba(4, 7, 14, 0.1);
}

.btp-chat__msg.is-own > div:last-child {
  background: linear-gradient(145deg, rgba(91, 124, 255, 0.93), rgba(91, 91, 218, 0.92));
  border-color: rgba(255, 255, 255, 0.12);
  border-radius: 18px 5px 18px 18px;
  box-shadow: 0 10px 26px rgba(46, 66, 162, 0.22);
}

.btp-chat__msg-avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  box-shadow: 0 7px 16px rgba(4, 7, 14, 0.17);
}

.btp-chat__msg-avatar-spacer { width: 38px; }
.btp-chat__msg.is-own .btp-chat__msg-header { justify-content: flex-end; }
.btp-chat__msg-author { color: var(--chat-text); font-size: 12px; }
.btp-chat__msg-time { color: var(--chat-subtle); font-size: 10px; }
.btp-chat__msg.is-own .btp-chat__msg-time { color: rgba(255, 255, 255, 0.7); }
.btp-chat__msg-text { color: #edf1f8; font-size: 13px; line-height: 1.58; }

.btp-chat__msg-img {
  width: min(100%, 360px);
  max-width: 100%;
  max-height: 300px;
  border-radius: 13px;
  border-color: var(--chat-border);
}

.btp-chat__msg-img-placeholder { max-width: 100%; background: rgba(255, 255, 255, 0.04); border-color: var(--chat-border); }

.btp-chat__msg-file {
  min-width: min(310px, 100%);
  max-width: 100%;
  background: rgba(8, 12, 21, 0.19);
  border-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.btp-chat__msg-file:hover { border-color: var(--chat-focus); }
.btp-chat__msg-file-meta { color: var(--chat-subtle); }

.btp-chat__typing { padding-left: 48px; color: var(--chat-muted); }
.typing-dots span { background: #8ea7ff; }

.btp-chat__footer {
  position: relative;
  z-index: 7;
  padding: 12px max(22px, calc((100% - 940px) / 2)) 13px;
  background: linear-gradient(180deg, rgba(23, 27, 39, 0), rgba(23, 27, 39, 0.9) 18%, #171b27 48%);
}

.btp-chat__attach-row { gap: 9px; margin-bottom: 9px; }

.btp-chat__attach {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  width: min(210px, 100%);
  height: 58px;
  padding: 6px 30px 6px 6px;
  overflow: visible;
  border-color: var(--chat-border);
  border-radius: 13px;
  background: var(--chat-panel);
}

.btp-chat__attach img,
.btp-chat__attach-file { width: 44px; height: 44px; border-radius: 9px; }
.btp-chat__attach-file { color: var(--chat-muted); background: rgba(91, 124, 255, 0.1); }
.btp-chat__attach-file .v-icon { color: #91a5ff !important; }

.btp-chat__attach-name {
  overflow: hidden;
  color: var(--chat-muted);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btp-chat__attach-remove {
  top: 7px;
  right: 7px;
  width: 20px;
  height: 20px;
  background: rgba(255, 115, 115, 0.85);
}

.btp-chat__input-box {
  overflow: hidden;
  background: rgba(34, 40, 56, 0.95);
  border-color: var(--chat-border);
  border-radius: 17px;
  box-shadow: 0 16px 38px rgba(4, 7, 14, 0.2);
}

.btp-chat__input-box:focus-within { border-color: var(--chat-focus); box-shadow: 0 0 0 3px rgba(142, 167, 255, 0.11), 0 16px 38px rgba(4, 7, 14, 0.2); }

.btp-chat__input-toolbar {
  min-height: 42px;
  padding: 5px 8px;
  gap: 3px;
  background: rgba(15, 19, 29, 0.18);
  border-color: var(--chat-border);
}

.btp-chat__tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 31px;
  padding: 5px 9px;
  color: var(--chat-muted);
  border-radius: 9px;
  font-size: 11px;
  font-weight: 650;
}

.btp-chat__tool-btn:hover { color: var(--chat-text); background: rgba(255, 255, 255, 0.065); }
.btp-chat__input-row { min-height: 58px; padding: 8px 9px 8px 13px; align-items: center; }

.btp-chat__input {
  min-height: 32px;
  padding: 6px 2px;
  color: var(--chat-text);
  font-size: 14px;
  line-height: 1.5;
}

.btp-chat__input::placeholder { color: #818ca1; }

.btp-chat__input-send {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: linear-gradient(145deg, #6f8bff, #536ff2 58%, #6759de);
  box-shadow: 0 8px 21px rgba(91, 124, 255, 0.28);
}

.btp-chat__input-send:hover:not(:disabled) { transform: translateY(-1px) scale(1.02); box-shadow: 0 11px 25px rgba(91, 124, 255, 0.37); }
.btp-chat__status { padding: 7px 3px 0; color: #818ca1; text-transform: none; letter-spacing: 0; }
.btp-chat__status strong { color: var(--chat-muted); font-weight: 700; }

.btp-chat__confirm {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 15px;
  padding: 22px;
  color: var(--chat-text);
  background: #202637;
  border: 1px solid var(--chat-border);
  border-radius: 20px;
  box-shadow: 0 26px 70px rgba(2, 5, 12, 0.55);
}

.btp-chat__confirm-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  color: var(--chat-danger);
  background: rgba(255, 115, 115, 0.1);
  border: 1px solid rgba(255, 115, 115, 0.2);
  border-radius: 15px;
}

.btp-chat__confirm h3 { margin: 4px 0 7px; font-size: 17px; }
.btp-chat__confirm p { margin: 0; color: var(--chat-muted); font-size: 13px; line-height: 1.55; }
.btp-chat__confirm-actions { grid-column: 1 / -1; display: flex; justify-content: flex-end; gap: 8px; padding-top: 4px; }

.btp-chat__delete-btn {
  color: #fff;
  background: linear-gradient(135deg, #f25f6f, #d9415b);
  box-shadow: 0 8px 20px rgba(239, 83, 108, 0.2);
}

button:focus-visible,
a:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 2px solid var(--chat-focus);
  outline-offset: 2px;
}

@media (max-width: 1100px) {
  .btp-chat__sidebar { flex-basis: 280px; width: 280px; max-width: 280px; }
  .btp-chat__search { width: 210px; }
  .btp-chat__connection { display: none; }
}

@media (max-width: 760px) {
  .chat-page {
    height: calc(100vh - 56px);
    height: calc(100dvh - 56px);
  }

  .btp-chat__sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 40;
    width: min(88vw, 340px);
    max-width: min(88vw, 340px);
    transform: translateX(-105%);
    transition: transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
    box-shadow: 24px 0 70px rgba(2, 5, 12, 0.48);
  }

  .btp-chat__sidebar.is-mobile-open { transform: translateX(0); }
  .btp-chat__channel-menu { opacity: 1; }

  .btp-chat__sidebar-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: var(--chat-muted);
    background: rgba(255, 255, 255, 0.045);
    border: 1px solid var(--chat-border);
    border-radius: 12px;
  }

  .btp-chat__sidebar-backdrop {
    position: fixed;
    inset: 0;
    z-index: 35;
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    background: rgba(5, 8, 15, 0.7);
    border: 0;
    backdrop-filter: blur(3px);
  }

  .btp-chat__main { flex-basis: 100%; width: 100%; max-width: 100%; }

  .btp-chat__header {
    min-height: 112px;
    height: auto;
    padding: 10px 13px;
    gap: 10px;
    flex-wrap: wrap;
    align-content: center;
  }

  .btp-chat__mobile-menu {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    color: var(--chat-text);
    background: rgba(255, 255, 255, 0.045);
    border: 1px solid var(--chat-border);
    border-radius: 13px;
  }

  .btp-chat__header-channel { flex: 1 1 calc(100% - 54px); }
  .btp-chat__header-channel-icon { flex-basis: 38px; width: 38px; height: 38px; }
  .btp-chat__header-desc { max-width: calc(100vw - 145px); }
  .btp-chat__header-actions { order: 3; width: 100%; }
  .btp-chat__search { width: auto; flex: 1; min-height: 38px; }
  .btp-chat__icon-btn { width: 38px; height: 38px; }

  .btp-chat__messages { padding: 18px 13px; }
  .btp-chat__msg { min-width: 0; max-width: 88%; }
  .btp-chat__msg-avatar,
  .btp-chat__msg-avatar-spacer { width: 34px; }
  .btp-chat__msg-avatar { height: 34px; border-radius: 10px; }
  .btp-chat__msg > div:last-child { padding: 9px 11px 10px; }
  .btp-chat__msg-file { min-width: min(240px, 100%); }
  .btp-chat__typing { padding-left: 43px; }

  .btp-chat__footer {
    padding: 9px 11px max(10px, env(safe-area-inset-bottom));
    background: #171b27;
    border-top: 1px solid var(--chat-border);
  }

  .btp-chat__empty { padding: 28px 18px; }
  .btp-chat__empty-halo { width: 94px; height: 94px; }
  .btp-chat__empty-mobile-action {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 8px 15px;
    color: #fff;
    background: linear-gradient(135deg, #5b7cff, #695de7);
    border: 0;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
  }

  .btp-chat__status > span:first-child { display: none; }
  .btp-chat__status { justify-content: flex-end; }
}

@media (max-width: 420px) {
  .btp-chat__brand { min-height: 76px; padding: 14px; }
  .btp-chat__channel { min-height: 50px; }
  .btp-chat__header { min-height: 108px; }
  .btp-chat__header-name { font-size: 14px; }
  .btp-chat__header-desc { max-width: calc(100vw - 140px); }
  .btp-chat__msg { max-width: 92%; gap: 7px; }
  .btp-chat__msg-avatar,
  .btp-chat__msg-avatar-spacer { width: 31px; }
  .btp-chat__msg-avatar { height: 31px; }
  .btp-chat__tool-btn { min-width: 36px; justify-content: center; padding: 5px 8px; }
  .btp-chat__tool-btn span { display: none; }
  .btp-chat__input { font-size: 16px; }
  .btp-chat__input-send { flex-basis: 40px; width: 40px; height: 40px; }
  .btp-chat__confirm { grid-template-columns: 44px minmax(0, 1fr); padding: 18px; }
  .btp-chat__confirm-icon { width: 44px; height: 44px; }
}

@media (prefers-reduced-motion: reduce) {
  .btp-chat__sidebar,
  .btp-chat__new-btn,
  .btp-chat__input-send,
  .typing-dots span { transition: none !important; animation: none !important; }
}
</style>
